import React, { useMemo } from 'react';

import { Flex, Form, Input, Modal } from 'antd-v5';
import { MdAdd, MdOutlineRemoveCircleOutline } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Select } from 'scouting-ui/lib';

import type { MBCYouthAssignationPayload } from '@appTypes/esb';
import { useAssignMBCToYouthMutation } from '@modules/meritBadgeCounselors/api';
import { useMeritBadgeCounselorProfile } from '@modules/meritBadgeCounselors/hooks';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import { dashboardYouthsSel } from '../duck/selectors';
import styles from './AssignYouthModal.module.scss';
import MBImage from './MBImage';

interface Props {
  mbcMemberId: string | number;
  showModal: boolean;
  onClose: VoidFunction;
}

interface addForm {
  youths: Array<{ LastName: string; MemberId: number; MbcIds: number[] }>;
}

enum FormFields {
  LastName = 'LastName',
  MemberId = 'MemberId',
  MbcIds = 'MbcIds',
}

export const BulkAssignYouthModal: React.FC<Props> = ({
  mbcMemberId,
  showModal,
  onClose,
}) => {
  const [form] = Form.useForm<addForm>();
  const [assignToYouth, { isLoading: isSaving }] =
    useAssignMBCToYouthMutation();
  const { meritBadges, loading: loadingProfile } =
    useMeritBadgeCounselorProfile({
      memberId: mbcMemberId as string,
      extendedInfo: true,
    });
  const youths = useSelector(dashboardYouthsSel);

  const handleClickSave = async () => {
    try {
      const valuesSubmitted = await form.validateFields();

      try {
        // Filters out merit badges or youths if they are already assigned with the same badges
        const dedupedYouths = valuesSubmitted.youths
          .map(formYouth => {
            const foundAssignedYouth = youths.find(
              youth => +formYouth.MemberId === youth.memberId,
            );
            if (foundAssignedYouth) {
              const filteredBadges = formYouth.MbcIds.filter(
                mbId =>
                  !foundAssignedYouth.meritBadgesCounseling.find(
                    badgeCounseling => badgeCounseling.meritBadgeId === mbId,
                  ),
              );
              return { ...formYouth, MbcIds: filteredBadges };
            }

            return formYouth;
          })
          .filter(formYouth => formYouth.MbcIds.length);

        if (dedupedYouths.length) {
          const payload: MBCYouthAssignationPayload[] = [
            {
              counselorMemberId: +mbcMemberId,
              youths: dedupedYouths.map(formData => ({
                youthLastName: formData.LastName,
                youthMemberId: +formData.MemberId,
                meritBadgesCounseling: formData.MbcIds.map(mbId => ({
                  meritBadgeId: mbId,
                })),
              })),
            },
          ];

          await assignToYouth({
            payload,
            invalidateExtra: [
              tagFactory.MeritBadgeCounselorProfile.mbcProfile(+mbcMemberId),
            ],
          }).unwrap();
        }
        toastService.success(
          <FormattedMessage id="mbcDashboard.addYouthModal.success" />,
        );
        onClose();
      } catch (error) {
        const errorData = error.data;
        if (errorData?.length) {
          const errorMessage = errorData
            .filter(({ processed }: { processed: boolean }) => !processed)
            .map(
              ({
                message,
                youthMemberId,
              }: {
                message: string;
                youthMemberId: number;
              }) => `${youthMemberId}: ${message}`,
            )
            .toString();
          toastService.error(errorMessage);
        }
        return;
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const options = useMemo(
    () =>
      meritBadges.map(badge => ({
        label: badge.meritBadgeName,
        value: badge.meritBadgeId,
        image: badge.imageUrl100,
      })),
    [meritBadges],
  );

  return (
    <Modal
      width={890}
      open={showModal}
      destroyOnClose
      title={<FormattedMessage id="mbcDashboard.addYouthModal.bulk" />}
      cancelText={<FormattedMessage id="shared.cancel" />}
      okText={<FormattedMessage id="shared.save" />}
      onOk={handleClickSave}
      cancelButtonProps={{
        type: 'default',
        onClick: onClose,
        disabled: isSaving,
      }}
      okButtonProps={{ loading: isSaving || loadingProfile }}
      onCancel={onClose}
    >
      <Form clearOnDestroy form={form} initialValues={{}}>
        <Form.List name="youths">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Flex
                  gap={4}
                  key={key}
                  wrap="wrap"
                  align="center"
                  justify="space-between"
                  className={styles.formContainer}
                >
                  <div className={styles.formItem}>
                    <Form.Item
                      {...restField}
                      name={[name, FormFields.LastName]}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'shared.form.error.isRequired',
                          }),
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: 'shared.personalInfo.lastName',
                        })}
                        size="small"
                      />
                    </Form.Item>
                  </div>
                  <div className={styles.formItem}>
                    <Form.Item
                      {...restField}
                      name={[name, FormFields.MemberId]}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'shared.form.error.isRequired',
                          }),
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: 'shared.memberId',
                        })}
                        size="small"
                      />
                    </Form.Item>
                  </div>
                  <div className={styles.formItem}>
                    <Form.Item
                      {...restField}
                      name={[name, FormFields.MbcIds]}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'shared.form.error.isRequired',
                          }),
                        },
                      ]}
                      noStyle
                    >
                      <Select
                        mode="multiple"
                        placeholder={intl.formatMessage({
                          id: 'mbcDashboard.addYouthModal.bulk.selectPlaceholder',
                        })}
                        optionFilterProp={'label'}
                        options={options}
                        optionRender={option => (
                          <Flex align="center" gap={4}>
                            <MBImage
                              height={'auto'}
                              width={24}
                              src={option.data.image}
                            />
                            {option.data.label}
                          </Flex>
                        )}
                      />
                    </Form.Item>
                  </div>
                  <Button
                    size="middle"
                    type="text"
                    shape="circle"
                    icon={<MdOutlineRemoveCircleOutline />}
                    onClick={() => remove(name)}
                  />
                </Flex>
              ))}
              <Form.Item>
                <Button
                  type="text"
                  icon={<MdAdd />}
                  onClick={() => add()}
                  block
                >
                  <FormattedMessage id="mbcDashboard.addYouthModal.bulk.addBttn" />
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};
